import React, { useEffect, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios'; // Import axios for API calls

import Dashboard from './pages/Dashboard';
import Login from './Login';
import Header from './component/Header1';
import EditMember from './pages/EditMember';
import EditTest from './pages/EditTest';
import EditChapter from './pages/EditChapter';
import MemberForm from './pages/MemberForm';
import MemberList from './pages/MemberList';
import CandiAdd from './pages/CandidateAdd';
import MemberDeleted from './pages/Memberdeleted';
import PreTest from './pages/PreTest';
import PreTestList from './pages/PreTestList';
import PriorEvidence from './pages/PriorEvidence';
import MemberReports from './pages/MemberReports';
import CandidateReports from './pages/CandidateReports';
import TestReports from './pages/TestReport'
import Test from './pages/Test';
import TestList from './pages/TestList';
import Testdeleted from './pages/Testdeleteds';
import InstituteList from './pages/InstituteList';
import InstituteForm from './pages/InstituteForm';
import BatchList from './pages/BatchList';
import BatchForm from './pages/BatchForm';
import BatchFeeApprovals from './pages/BatchFeeApprovals';
import BatchDashboard from './pages/BatchDashboard';
import BatchTestForm from './pages/BatchTestForm';
import BatchTestDetail from './pages/BatchTestDetail';
import EventDeleted from './pages/Eventdeleteds';
import Newsevent from './pages/NewsEventForm';
import Newseventlist from './pages/NewsEventList';
import ChapterList from './pages/ChapterList';
import Chapter from './pages/Chapter';
import Testimoniallist from './pages/Testimoniallist';
import AdminProfile from './pages/AdminProfile';
import Manageadmin from './pages/Manageadmin';
import PriortestAccept from './pages/PriortestAccept';
import ContactsAdd from './pages/ContactsAdd';
import MIndMagazine from './pages/MindMagazine';
import Addadmin from './pages/Addadmin';
import EditAdmin from './pages/Editadmin';
import AdminDeleted from './pages/Admindeleted';
import Interchapter from './pages/Interchapter';
import International from './pages/International';
import CardList from './pages/CardList';
import PlaqueList from './pages/PlaqueList';
import Transactionlist from './pages/Transactionlist';
import EmailSetting from './pages/EmailSetting';
import PaymentSetting from './pages/PaymentSetting';
import Fees from './pages/FeesPage';
import WebsiteCounting from './pages/WebsiteCounting';
import { baseurl } from './utility/helper';

const validateToken = async (token) => {
  try {
    const response = await axios.post(`${baseurl}/admin/validate-token`, { token });
    return response.data.isValid;
  } catch (error) {
    console.error("Token validation failed:", error);
    return false;
  }
};

const SYNC_REQUEST_KEY = 'sessionStorage_sync_request';
const SYNC_RESPONSE_KEY = 'sessionStorage_sync_response';

// Any tab that already has a session responds to sync requests from
// other tabs. Attached once per tab, at module load.
window.addEventListener('storage', (event) => {
  if (event.key === SYNC_REQUEST_KEY && event.newValue) {
    if (!sessionStorage.getItem('token')) return; // this tab has nothing to share
    const data = {};
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      data[key] = sessionStorage.getItem(key);
    }
    localStorage.setItem(SYNC_RESPONSE_KEY, JSON.stringify(data));
    localStorage.removeItem(SYNC_RESPONSE_KEY);
  }
});

let syncPromise = null;

// Call this once before trusting sessionStorage in a freshly opened tab.
const syncSessionStorageFromOtherTabs = () => {
  if (sessionStorage.getItem('token')) {
    return Promise.resolve(); // already have a session, nothing to do
  }
  if (syncPromise) return syncPromise;

  syncPromise = new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener('storage', onResponse);
      resolve();
    };

    function onResponse(event) {
      if (event.key === SYNC_RESPONSE_KEY && event.newValue) {
        try {
          const data = JSON.parse(event.newValue);
          Object.keys(data).forEach((k) => sessionStorage.setItem(k, data[k]));
        } catch (e) {
          console.error('sessionStorage sync parse failed:', e);
        }
        finish();
      }
    }

    window.addEventListener('storage', onResponse);
    localStorage.setItem(SYNC_REQUEST_KEY, Date.now().toString());
    localStorage.removeItem(SYNC_REQUEST_KEY);

    // No other logged-in tab responded in time (or none exists) — give up
    // and let the normal "no token" redirect to /admin happen.
    setTimeout(finish, 400);
  });

  return syncPromise;
};

// ── Session-end handling shared by every tab ────────────────────────────
// Tabs copy the session from each other (above), so when it dies in one tab
// (server rejects the token, or the user logs out) every other tab has to
// drop it too — otherwise a tab opened earlier keeps showing the old page.
const AUTH_LOGOUT_KEY = 'auth_logout';
let loggingOut = false;

const forceLogout = (broadcast = true) => {
  if (loggingOut) return;
  loggingOut = true;
  sessionStorage.clear();
  if (broadcast) {
    try {
      localStorage.setItem(AUTH_LOGOUT_KEY, Date.now().toString());
      localStorage.removeItem(AUTH_LOGOUT_KEY);
    } catch (e) { /* ignore */ }
  }
  window.location.replace('/admin');
};

// Another tab logged out / expired (Header1 logout calls localStorage.clear(),
// which arrives here as key === null).
window.addEventListener('storage', (event) => {
  if ((event.key === AUTH_LOGOUT_KEY && event.newValue) || event.key === null) {
    if (window.location.pathname !== '/admin') forceLogout(false);
  }
});

// Any API call rejected with 401 while we hold a token means the session is dead.
axios.interceptors.response.use(
  (res) => res,
  (error) => {
    const url = error?.config?.url || '';
    if (
      error?.response?.status === 401 &&
      sessionStorage.getItem('token') &&
      !url.includes('/admin/validate-token')
    ) {
      forceLogout();
    }
    return Promise.reject(error);
  }
);

const SESSION_CHECK_INTERVAL_MS = 60 * 1000;

const ProtectedRoute = ({ element }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    syncSessionStorageFromOtherTabs().then(() => {
      if (cancelled) return;
      const token = sessionStorage.getItem("token");
      if (!token) {
        navigate("/admin");
        return;
      }
      axios.defaults.headers.common['Authorization'] = `${token}`;

      validateToken(token)
        .then((isValid) => {
          if (cancelled) return;
          if (!isValid) forceLogout();
        })
        .catch(() => {
          if (!cancelled) navigate("/admin");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    });

    return () => { cancelled = true; };
  }, [navigate]);

  if (loading) return <div>Loading...</div>;
  return element;
};

// Role-Based Protected Route
const RoleProtectedRoute = ({ element, allowedRoles }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    syncSessionStorageFromOtherTabs().then(() => {
      if (cancelled) return;
      const roleid = parseInt(sessionStorage.getItem("userroleId"));
      if (!allowedRoles.includes(roleid)) {
        navigate("/admin", { replace: true });
        return;
      }
      setAllowed(true);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [navigate, allowedRoles]);

  if (loading) return <div>Loading...</div>;
  if (!allowed) return null;

  return <ProtectedRoute element={element} />;
};

const AppContent = () => {
  const location = useLocation();
  const showHeader = location.pathname !== "/admin";
  const showPageWrapper = location.pathname !== "/admin";

  // ── Sidebar sync-gate ──────────────────────────────────────────────────
  // Header reads sessionStorage synchronously on its first render to decide
  // which role-gated sidebar links to show. On a freshly opened tab (e.g.
  // right-click → "open in new tab" on a sidebar link) sessionStorage starts
  // empty, and Header used to mount immediately — before
  // syncSessionStorageFromOtherTabs() had copied the session over from
  // another logged-in tab — so it built the sidebar with no role and never
  // got told to re-render once the sync finished. The route content itself
  // was fine (ProtectedRoute/RoleProtectedRoute already await the sync), but
  // the sidebar stayed stuck showing only the un-gated links.
  // Fix: hold off mounting Header/Routes until this same sync has settled.
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    syncSessionStorageFromOtherTabs().then(() => {
      if (!cancelled) setSessionReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  // Re-check the token every minute and whenever the tab regains focus, so a
  // tab that was left open notices the session ended elsewhere.
  useEffect(() => {
    const check = () => {
      const token = sessionStorage.getItem('token');
      if (!token || window.location.pathname === '/admin') return;
      axios.post(`${baseurl}/admin/validate-token`, { token })
        .then((res) => { if (res.data.isValid === false) forceLogout(); })
        .catch(() => { /* network blip: keep the session */ });
    };
    const id = setInterval(check, SESSION_CHECK_INTERVAL_MS);
    window.addEventListener('focus', check);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', check);
    };
  }, []);

  useEffect(() => {
    if (location.pathname === "/admin/dashboard" &&localStorage.getItem("reloaded") === "false") {
      localStorage.setItem("reloaded", "true");
      window.location.reload();
    }
  }, [location]);

  if (!sessionReady) return <div>Loading...</div>;

  return (
    <>
      {showHeader && <Header />}
      {showPageWrapper ? (
        <div className="page-wrapper">
          <Routes>

            {/* ✅ Accessible by ALL roles (1, 2, 4) */}
            <Route path="/admin/Dashboard"       element={<RoleProtectedRoute element={<Dashboard />}     allowedRoles={[1, 2, 4, 3]}/>} />
            <Route path="/admin/AdminProfile"    element={<RoleProtectedRoute element={<AdminProfile />}  allowedRoles={[1, 2, 4, 3]}/>} />
            <Route path="/admin/TestList"        element={<RoleProtectedRoute element={<TestList />}      allowedRoles={[1, 2, 4, 3]}/>} />
            <Route path="/admin/Test"            element={<RoleProtectedRoute element={<Test />}          allowedRoles={[1, 2, 4]}/>} />
            <Route path="/admin/EditTest"        element={<RoleProtectedRoute element={<EditTest />}      allowedRoles={[1, 2, 4, 3]}/>} />
            <Route path="/admin/EditTest/:testId" element={<RoleProtectedRoute element={<EditTest />}     allowedRoles={[1, 2, 4]}/>} />
            <Route path="/admin/PriorEvidence"   element={<RoleProtectedRoute element={<PriorEvidence />}   allowedRoles={[1, 4]} />} />

            {/* ── Institute Test Management (Institute → Batch → Test) ── */}
            <Route path="/admin/InstituteList"     element={<RoleProtectedRoute element={<InstituteList />}     allowedRoles={[1]} />} />
            <Route path="/admin/InstituteForm"     element={<RoleProtectedRoute element={<InstituteForm />}     allowedRoles={[1]} />} />
            <Route path="/admin/BatchFeeApprovals" element={<RoleProtectedRoute element={<BatchFeeApprovals />} allowedRoles={[1]} />} />
            <Route path="/admin/BatchList"         element={<RoleProtectedRoute element={<BatchList />}         allowedRoles={[1, 2]} />} />
            <Route path="/admin/BatchForm"         element={<RoleProtectedRoute element={<BatchForm />}         allowedRoles={[1, 2]} />} />
            <Route path="/admin/BatchDashboard"    element={<RoleProtectedRoute element={<BatchDashboard />}    allowedRoles={[1, 2]} />} />
            <Route path="/admin/BatchTestForm"     element={<RoleProtectedRoute element={<BatchTestForm />}     allowedRoles={[1, 2]} />} />
            <Route path="/admin/BatchTestDetail"   element={<RoleProtectedRoute element={<BatchTestDetail />}   allowedRoles={[1, 2]} />} />

            {/* ✅ Accessible by roles 1 (SuperAdmin) and 2 (Admin) — NOT role 4 */}
            <Route path="/admin/MemberList"      element={<RoleProtectedRoute element={<MemberList />}      allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/MemberList/:chapterId?" element={<RoleProtectedRoute element={<MemberList />} allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/CandiAdd"        element={<RoleProtectedRoute element={<CandiAdd />}        allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/CandiAdd/:candidateId" element={<RoleProtectedRoute element={<CandiAdd />} allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/MemberForm"      element={<RoleProtectedRoute element={<MemberForm />}      allowedRoles={[1, 2]} />} />
            <Route path="/admin/EditMember"      element={<RoleProtectedRoute element={<EditMember />}      allowedRoles={[1, 2, 3]} />} />
            {/* <Route path="/admin/EditMember/" element={<RoleProtectedRoute element={<EditMember />} allowedRoles={[1, 2, 3]} />} /> */}
            <Route path="/admin/EditMember/*"    element={<RoleProtectedRoute element={<EditMember />}      allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/Memberdeleted"   element={<RoleProtectedRoute element={<MemberDeleted />}   allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/Newsevent"       element={<RoleProtectedRoute element={<Newsevent />}       allowedRoles={[1, 2]} />} />
            <Route path="/admin/Newseventlist"   element={<RoleProtectedRoute element={<Newseventlist />}   allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/Newsevent/:id/edit" element={<RoleProtectedRoute element={<Newsevent />}    allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/Eventdeleted"    element={<RoleProtectedRoute element={<EventDeleted />}    allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/Testimoniallist" element={<RoleProtectedRoute element={<Testimoniallist />} allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/MemberReports"   element={<RoleProtectedRoute element={<MemberReports />}   allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/CandidateReports"   element={<RoleProtectedRoute element={<CandidateReports />}   allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/TestReports"     element={<RoleProtectedRoute element={<TestReports />}     allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/Transactionlist" element={<RoleProtectedRoute element={<Transactionlist />} allowedRoles={[1, 2, 5, 3]} />} />
            <Route path="/admin/Interchapter"    element={<RoleProtectedRoute element={<Interchapter />}    allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/International"   element={<RoleProtectedRoute element={<International />}   allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/CardList"        element={<RoleProtectedRoute element={<CardList />}        allowedRoles={[1, 2, 3]} />} />
            <Route path="/admin/PlaqueList"      element={<RoleProtectedRoute element={<PlaqueList />}      allowedRoles={[1, 2, 3]} />} />

            {/* ✅ Accessible by role 1 (SuperAdmin) ONLY */}
            <Route path="/admin/PreTest"         element={<RoleProtectedRoute element={<PreTest />}         allowedRoles={[1]} />} />
            <Route path="/admin/PreTestList"     element={<RoleProtectedRoute element={<PreTestList />}     allowedRoles={[1]} />} />
            
            <Route path="/admin/ChapterList"     element={<RoleProtectedRoute element={<ChapterList />}     allowedRoles={[1]} />} />
            <Route path="/admin/Chapter"         element={<RoleProtectedRoute element={<Chapter />}         allowedRoles={[1]} />} />
            <Route path="/admin/EditChapter"     element={<RoleProtectedRoute element={<EditChapter />}     allowedRoles={[1]} />} />
            <Route path="/admin/EditChapter/:chapterId" element={<RoleProtectedRoute element={<EditChapter />} allowedRoles={[1]} />} />
            <Route path="/admin/Manageadmin"     element={<RoleProtectedRoute element={<Manageadmin />}     allowedRoles={[1]} />} />
            <Route path="/admin/Addadmin"        element={<RoleProtectedRoute element={<Addadmin />}        allowedRoles={[1]} />} />
            <Route path="/admin/EditAdmin"       element={<RoleProtectedRoute element={<EditAdmin />}       allowedRoles={[1]} />} />
            <Route path="/admin/Admindeleted"    element={<RoleProtectedRoute element={<AdminDeleted />}    allowedRoles={[1]} />} />
            <Route path="/admin/Testdeleted"     element={<RoleProtectedRoute element={<Testdeleted />}     allowedRoles={[1]} />} />
            <Route path="/admin/PriortestAccept" element={<RoleProtectedRoute element={<PriortestAccept />} allowedRoles={[1]} />} />
            <Route path="/admin/ContactsAdd"     element={<RoleProtectedRoute element={<ContactsAdd />}     allowedRoles={[1]} />} />
            <Route path="/admin/MIndMagazine"    element={<RoleProtectedRoute element={<MIndMagazine />}    allowedRoles={[1]} />} />
            <Route path="/admin/WebsiteCounting" element={<RoleProtectedRoute element={<WebsiteCounting />} allowedRoles={[1]} />} />
            <Route path="/admin/Fees"            element={<RoleProtectedRoute element={<Fees />}            allowedRoles={[1]} />} />
            <Route path="/admin/EmailSetting"    element={<RoleProtectedRoute element={<EmailSetting />}    allowedRoles={[1]} />} />
            <Route path="/admin/PaymentSetting"  element={<RoleProtectedRoute element={<PaymentSetting />}  allowedRoles={[1]} />} />

            {/* Login & fallback */}
            <Route path="/admin" element={<Login />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />

          </Routes>
        </div>
      ) : (
        <Routes>
          <Route path="/admin" element={<Login />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      )}
    </>
  );
};

const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;